import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { fetchPage } from '@/lists/api'
import { CellValue } from '@/lists/cellValue'
import { BalancesPanel } from './EmployeeRecordView'
import { HistoryPanel } from './HistoryPanel'
import {
  approveLeave,
  cancelLeave,
  fetchBalances,
  fetchClashes,
  fetchCoverage,
  fetchRoute,
  fetchTimesheet,
  rejectLeave,
  type CoverageView,
  type LeaveRequestCard,
  type RouteStepView,
  type TimeEntryView,
} from './hrApi'
import {
  RefLink,
  Section,
  StatusBadge,
  calendarDays,
  day,
  isoDate,
  nameOf,
  num,
  parseDay,
  text,
  useEmployeeNames,
  useOutcome,
  OutcomeAlerts,
  type Translate,
} from './hrShared'
import { ConfirmModal, Facts, Panel, RecordHeader, RecordTabs, StageBar, StateView, type Fact, type HeaderAction, type MenuAction } from './record'
import { labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import '@/styles/record.css'

/**
 * Карточка заявки на отсутствие на дизайн-системе (LeaveRequest.dc.html).
 *
 * ## Сквозной сценарий тикета
 *
 * Сотрудник подаёт заявку → она уходит по маршруту из оргструктуры → **руководитель видит
 * пересечения с отпусками коллег до одобрения**. Кнопки «Согласовать» и «Отклонить» стоят в
 * шапке, а пересечения — на вкладке «Обзор», открытой по умолчанию: на экране решения они
 * видны вместе, и догадываться, куда перейти, не нужно. Вкладка, на которую надо
 * догадаться перейти, не показана никому.
 *
 * Два человека из отдела в отпуске одну неделю не запрещены — `thin` (отдел останется без
 * трети) это сигнал, а не запрет: решает человек, а кнопка остаётся доступной.
 *
 * ## Откуда заявка
 *
 * `GET /hr/leave-requests/{id}` на сервере нет: заявка собирается из строки, которую карточка
 * уже прочитала механизмом списков. Нехватка сотрудника или дат — **отказ**, а не заявка из
 * пустых строк: такая уехала бы запросом пересечений с датами `""`, и на экране это выглядело
 * бы поломкой сети. Подразделения у заявки своей колонки нет — оно у сотрудника, и читается
 * строкой сотрудника из того же механизма. Не прочиталось — «подразделение неизвестно», что
 * не равно «пересечений нет».
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - «Предложить другие даты»: отказ несёт причину (`POST /reject {reason}`, V53) — она
 *   обязательна и видна на карточке, — но встречного предложения дат у сервера нет.
 * - Номер заявки (ОТП-…): у заявки нет номера.
 * - Пошаговый статус маршрута (кто уже согласовал): решение по заявке принимается одним
 *   вызовом, и сервер не отмечает шаги — маршрут показан как план, построенный тем же
 *   правилом, что и при подаче.
 * - Клиенты и сделки сотрудника на период, замещающий: этого API нет.
 * - На месте по дням: покрытие приходит одной цифрой на период, а не по дням.
 */
export function LeaveRecordView({ recordId, record, context }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const [tab, setTab] = useState('overview')
  const [confirmReject, setConfirmReject] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [reason, setReason] = useState('')
  const values = record.values
  const request = leaveFromRow(recordId, values)
  const mayApprove = can(user, 'hr.leave', 'approve')
  const mayUpdate = can(user, 'hr.leave', 'update')

  const outcome = useOutcome([
    ['record', 'hr.leave', recordId],
    ['list', 'hr.leave'],
    ['hr-balances', request?.employeeId ?? ''],
  ])
  const approve = useMutation({
    mutationFn: () => approveLeave(recordId),
    onSuccess: () => outcome.succeed(t('card.hr.leave.approved')),
    onError: outcome.fail,
  })
  const reject = useMutation({
    mutationFn: () => rejectLeave(recordId, reason.trim()),
    onSuccess: () => {
      setConfirmReject(false)
      setReason('')
      outcome.succeed(t('card.hr.leave.rejected'))
    },
    onError: (error) => {
      setConfirmReject(false)
      outcome.fail(error)
    },
  })

  // #94: cancelled as of today. Allowed after approval while the leave is ahead — the
  // days drawn from the balance come back; the domain decides what is too late.
  const cancel = useMutation({
    mutationFn: () => cancelLeave(recordId, todayIso()),
    onSuccess: () => {
      setConfirmCancel(false)
      outcome.succeed(t('record.hr.leave.cancelled'))
    },
    onError: (error) => {
      setConfirmCancel(false)
      outcome.fail(error)
    },
  })

  const listTo = listPath('hr.leave', '/leaves')
  if (!request) {
    // A request without its employee or dates is a refusal, not an empty request: it would
    // otherwise go out as a clashes query with dates "".
    return (
      <div className="rec-page">
        <div className="state-box">
          <StateView kind="error" title={t('record.hr.leave.rowMissingTitle')} description={t('card.hr.leave.rowMissing')} />
        </div>
      </div>
    )
  }

  // The decision is taken on a pending request only: the domain refuses the rest, and a
  // button that always fails teaches people to ignore failures.
  const decidable = request.status === 'pending'
  const decided = approve.isSuccess || reject.isSuccess
  const primary: HeaderAction | null =
    mayApprove && decidable && !decided
      ? { id: 'approve', label: t('card.hr.leave.approve'), icon: 'check', onClick: () => approve.mutate(), disabled: approve.isPending }
      : null
  const secondary: HeaderAction[] =
    mayApprove && decidable && !decided ? [{ id: 'reject', label: t('record.hr.leave.rejectOpen'), icon: 'close', onClick: () => setConfirmReject(true) }] : []
  const menu: MenuAction[] = [
    {
      id: 'copy-link',
      label: t('record.copyLink'),
      icon: 'link',
      onSelect: () => void navigator.clipboard?.writeText(window.location.href).catch(() => undefined),
    },
  ]
  if (mayUpdate && (request.status === 'pending' || request.status === 'approved') && !cancel.isSuccess) {
    menu.push({ id: 'cancel', label: t('record.hr.leave.cancel'), icon: 'close', danger: true, onSelect: () => setConfirmCancel(true) })
  }

  const employeeName = labelOf(values, 'employee_id')
  const title = t('record.hr.leave.title', {
    type: typeLabel(request.type, t),
    from: day(request.from, locale),
    to: day(request.to, locale),
  })
  const calendar = calendarDays(request.from, request.to)
  const createdAt = text(values, 'created_at')

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.leaves'), to: listTo }, { label: employeeName ?? t('record.hr.leave.request') }]}
        title={
          <span className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            {title}
            <StatusBadge entity="hr.leave" status={request.status} />
          </span>
        }
        meta={[
          employeeName ? <RefLink key="employee" values={values} column="employee_id" entity="hr.employee" avatar /> : null,
          <DepartmentMeta key="department" values={values} employeeId={request.employeeId} />,
        ]}
        primary={primary}
        secondary={secondary}
        menu={menu}
        menuLabel={t('record.more')}
      />

      <OutcomeAlerts failure={outcome.failure} notice={outcome.notice} />
      {/* Said in words, not by an empty header: the reader looks for the buttons. */}
      {mayApprove && !decidable && <p className="muted t-sm">{t('card.hr.leave.decisionUnavailable')}</p>}

      <StageBar
        ariaLabel={t('record.hr.leave.stages')}
        steps={[
          {
            id: 'submitted',
            label: t('record.hr.leave.submitted'),
            state: request.status === 'draft' ? 'current' : 'done',
            meta: createdAt ? dayOfMoment(createdAt, locale) : undefined,
          },
          {
            id: 'pending',
            label: t('value.hr.leave.status.pending'),
            state: request.status === 'pending' ? 'current' : request.status === 'draft' ? 'future' : 'done',
          },
        ]}
        ends={[
          { id: 'approved', label: t('value.hr.leave.status.approved'), tone: 'won', active: request.status === 'approved' },
          {
            id: 'rejected',
            label: request.status === 'cancelled' ? t('value.hr.leave.status.cancelled') : t('value.hr.leave.status.rejected'),
            tone: 'lost',
            active: request.status === 'rejected' || request.status === 'cancelled',
          },
        ]}
      />

      <Facts
        ariaLabel={t('record.facts')}
        items={
          [
            { id: 'type', label: t('column.leave_type'), value: <span className="t-md" style={{ fontWeight: 500, whiteSpace: 'normal' }}>{typeLabel(request.type, t)}</span> },
            {
              id: 'period',
              label: t('record.hr.leave.period'),
              value: `${day(request.from, locale)} — ${day(request.to, locale)}`,
              sub: calendar != null ? t('record.hr.leave.calendarDays', { days: calendar, back: day(nextDay(request.to), locale) }) : undefined,
            },
            { id: 'working', label: t('record.hr.leave.workingDaysFact'), value: num(request.daysCount, locale), sub: t('record.hr.leave.workingNote') },
            { id: 'balance', label: t('record.hr.leave.balanceAfter'), value: <BalanceFact request={request} />, sub: undefined },
            { id: 'submitted', label: t('record.hr.leave.submitted'), value: createdAt ? dayOfMoment(createdAt, locale) : '—' },
          ] satisfies Fact[]
        }
      />

      <RecordTabs
        tabs={[
          { id: 'overview', label: t('record.tab.overview') },
          { id: 'timesheet', label: t('card.tab.hrTimesheet') },
          { id: 'balances', label: t('record.hr.leave.balancesTab') },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.hr.leave.sections')}
      >
        <div style={{ paddingTop: 14 }}>
          {tab === 'overview' && (
            <div className="layout-2-wide">
              <div className="rec-col">
                <OverlapPanel request={request} recordId={recordId} values={values} />
              </div>
              <div className="rec-col">
                <RoutePanel request={request} />
                {request.status === 'rejected' && (
                  <Panel title={t('record.hr.leave.rejectionReason')}>
                    <div className="feed-body" style={{ marginTop: 0 }}>
                      {/* Refusals before V53 have no reason: said so, not left blank. */}
                      {request.rejectionReason ?? <span className="muted">{t('record.hr.leave.noRejectionReason')}</span>}
                    </div>
                  </Panel>
                )}
                {request.comment && (
                  <Panel title={t('record.hr.leave.comment')}>
                    <div className="feed-body" style={{ marginTop: 0 }}>
                      {request.comment}
                    </div>
                  </Panel>
                )}
              </div>
            </div>
          )}
          {tab === 'timesheet' && <TimesheetPanel employeeId={request.employeeId} from={request.from} to={request.to} />}
          {tab === 'balances' && <BalancesPanel employeeId={request.employeeId} />}
          {tab === 'history' && (
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity={context.entity} recordId={recordId} />
            </Panel>
          )}
        </div>
      </RecordTabs>

      <ConfirmModal
        open={confirmReject}
        icon="close"
        title={t('record.hr.leave.rejectTitle', { name: employeeName ?? '' })}
        confirmLabel={t('record.hr.leave.rejectConfirm')}
        pending={reject.isPending}
        confirmDisabled={reason.trim() === ''}
        onCancel={() => setConfirmReject(false)}
        onConfirm={() => reject.mutate()}
      >
        <p>{t('record.hr.leave.rejectText')}</p>
        <div className="field">
          <label className="label" htmlFor="leave-reject-reason">
            {t('record.hr.leave.rejectionReason')}
            <span className="req" aria-hidden="true">
              {' '}
              *
            </span>
          </label>
          <textarea
            id="leave-reject-reason"
            className="input"
            aria-required="true"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {/* Required, and said so before the click rather than by a refusal after it. */}
          <span className="help">{t('record.hr.leave.rejectReasonHelp')}</span>
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={confirmCancel}
        icon="close"
        title={t('record.hr.leave.cancelTitle')}
        confirmLabel={t('record.hr.leave.cancel')}
        pending={cancel.isPending}
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => cancel.mutate()}
      >
        <p>{t('record.hr.leave.cancelText')}</p>
      </ConfirmModal>
    </div>
  )
}

/** Today as a calendar date in local time — the date the cancellation takes effect. */
function todayIso(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/**
 * Собирает заявку из строки, прочитанной механизмом списков. Нет сотрудника или дат —
 * `null`, то есть отказ, а не заявка с пустыми полями.
 */
export function leaveFromRow(id: string, values: Record<string, unknown> | null | undefined): LeaveRequestCard | null {
  if (!values) return null
  const employeeId = text(values, 'employee_id')
  const from = text(values, 'start_date')
  const to = text(values, 'end_date')
  if (employeeId == null || from == null || to == null) return null
  const daysCount = typeof values.days_count === 'number' ? values.days_count : Number(values.days_count ?? NaN)
  return {
    id,
    employeeId,
    type: text(values, 'leave_type') ?? '',
    from,
    to,
    daysCount: Number.isFinite(daysCount) ? daysCount : 0,
    status: text(values, 'status') ?? '',
    comment: text(values, 'comment'),
    rejectionReason: text(values, 'rejection_reason'),
  }
}

/**
 * Подразделение сотрудника — его строкой в механизме списков.
 *
 * Строка заявки подразделения не несёт; если строка сотрудника не прочиталась (нет права
 * на кадры), подразделение `null` — и это «неизвестно», а не «нет».
 */
function useDepartment(values: Record<string, unknown>, employeeId: string) {
  const own = text(values, 'department_id')
  const query = useQuery({
    queryKey: ['hr-leave-employee-row', employeeId],
    enabled: own == null,
    retry: false,
    queryFn: ({ signal }) =>
      fetchPage(
        'hr.employee',
        { columns: ['department_id'], filters: [{ column: 'id', operator: 'eq', value: employeeId }], sort: [], pageSize: 1 },
        null,
        signal,
      ),
  })
  if (own != null) return { id: own, label: labelOf(values, 'department_id'), loading: false }
  const row = query.data?.rows[0]
  return {
    id: row ? text(row, 'department_id') : null,
    label: row ? labelOf(row, 'department_id') : null,
    loading: query.isLoading,
  }
}

function DepartmentMeta({ values, employeeId }: { values: Record<string, unknown>; employeeId: string }) {
  const department = useDepartment(values, employeeId)
  if (!department.id || !department.label) return null
  return (
    <span className="row" style={{ gap: 6 }}>
      <Icon name="building" size="sm" />
      <RefLink values={{ department_id: department.id, department_id__label: department.label }} column="department_id" entity="hr.department" />
    </span>
  )
}

/** Остаток после отпуска — с сервера; здесь только подписывается, ничего не вычитается. */
function BalanceFact({ request }: { request: LeaveRequestCard }) {
  const { t, locale, user } = useApp()
  const mayRead = can(user, 'hr.leave', 'read')
  const year = Number(request.from.slice(0, 4))
  const query = useQuery({
    queryKey: ['hr-balances', request.employeeId],
    queryFn: ({ signal }) => fetchBalances(request.employeeId, signal),
    enabled: mayRead,
    retry: false,
  })
  const row = (query.data ?? []).find((balance) => balance.year === year && balance.type === request.type)
  if (!row) return <>—</>
  return <>{t('record.hr.days', { days: num(row.remaining, locale) })}</>
}

// ------------------------------------------------------------------ пересечения

/**
 * Пересечения с отпусками коллег (`.overlap`).
 *
 * Своя заявка из списка коллег убирается: она пересекается сама с собой всегда, и строка о
 * себе среди коллег читалась бы как ещё один отсутствующий. Сама заявка рисуется первой
 * строкой полосой «эта заявка», чтобы было с чем сравнивать.
 */
export function OverlapPanel({
  request,
  recordId,
  values,
}: {
  request: LeaveRequestCard
  recordId: string
  values: Record<string, unknown>
}) {
  const { t, locale } = useApp()
  const department = useDepartment(values, request.employeeId)
  const departmentId = department.id

  const clashes = useQuery({
    queryKey: ['hr-leave-clashes', departmentId, request.from, request.to],
    queryFn: ({ signal }) => fetchClashes(departmentId as string, request.from, request.to, signal),
    enabled: departmentId != null,
    retry: false,
  })
  const coverage = useQuery({
    queryKey: ['hr-leave-coverage', departmentId, request.from, request.to],
    queryFn: ({ signal }) => fetchCoverage(departmentId as string, request.from, request.to, signal),
    enabled: departmentId != null,
    retry: false,
  })
  const others = (clashes.data ?? []).filter((row) => row.id !== recordId)
  const names = useEmployeeNames([request.employeeId, ...others.map((row) => row.employeeId)])

  return (
    <Panel
      title={t('card.hr.leave.clashes')}
      actions={<span className="t-sm muted">{`${day(request.from, locale)} — ${day(request.to, locale)}`}</span>}
    >
      <div className="stack" style={{ gap: 14 }}>
        {departmentId == null ? (
          department.loading ? (
            <p className="muted">{t('app.loading')}</p>
          ) : (
            // "Department unknown" is not "no clashes": an empty list here would pass off
            // not knowing as permission.
            <div className="alert alert-neutral" role="status">
              <Icon name="alert" />
              <span>{t('card.hr.leave.clashesUnknownDepartment')}</span>
            </div>
          )
        ) : (
          <Section query={clashes} errorTitle={t('record.hr.leave.clashesFailed')}>
            {() => (
              <>
                {others.length === 0 ? (
                  <div className="alert alert-success" role="status">
                    <Icon name="check" />
                    <span className="fg">{t('card.hr.leave.noClashes')}</span>
                  </div>
                ) : (
                  <div className="alert alert-warning" role="status">
                    <Icon name="alert" />
                    <span className="fg">{t('record.hr.leave.clashCount', { count: others.length })}</span>
                  </div>
                )}
                {coverage.data && <CoverageLine data={coverage.data} />}
                <OverlapChart request={request} others={others} names={names} />
              </>
            )}
          </Section>
        )}
      </div>
    </Panel>
  )
}

/** Покрытие: `thin` — сигнал словом и иконкой, кнопка решения остаётся доступной. */
function CoverageLine({ data }: { data: CoverageView }) {
  const { t } = useApp()
  return (
    <p className={data.thin ? 'flag flag-warning' : 'secondary'} style={{ whiteSpace: 'normal', margin: 0 }}>
      {data.thin && <Icon name="alert" />}
      {t('card.hr.leave.coverage', { away: data.away, headcount: data.headcount, present: data.present })}
      {data.thin ? ` ${t('card.hr.leave.thin')}` : ''}
    </p>
  )
}

/** Окно шкалы — период заявки; длиннее двух месяцев шкала нечитаема, остаётся список. */
const MAX_SCALE_DAYS = 62

function OverlapChart({
  request,
  others,
  names,
}: {
  request: LeaveRequestCard
  others: LeaveRequestCard[]
  names: Map<string, string>
}) {
  const { t, locale } = useApp()
  const start = parseDay(request.from)
  const total = calendarDays(request.from, request.to)
  const scaled = start != null && total != null && total > 0 && total <= MAX_SCALE_DAYS
  const days = scaled ? Array.from({ length: total }, (_, index) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + index)) : []
  const offset = (iso: string) => {
    const at = parseDay(iso)
    return at && start ? Math.round((at.getTime() - start.getTime()) / 86_400_000) : 0
  }
  const bar = (from: string, to: string, className: string, label: string) => {
    if (!scaled || total == null) return null
    const left = Math.max(offset(from), 0)
    const right = Math.min(offset(to), total - 1)
    if (right < left) return null
    return (
      <span key={`${className}-${from}`} className={className} style={{ left: `${(left / total) * 100}%`, width: `${((right - left + 1) / total) * 100}%` }}>
        {label}
      </span>
    )
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="overlap" role="group" aria-label={t('record.hr.leave.overlapLabel')}>
        {scaled && (
          <>
            <span className="t-xs muted">{t('record.hr.leave.scaleCaption')}</span>
            <div className="ol-scale" style={{ ['--days' as string]: String(total) }}>
              {days.map((date) => (
                <span key={isoDate(date)} className={date.getDay() === 0 || date.getDay() === 6 ? 'we' : undefined}>
                  {date.getDate()}
                </span>
              ))}
            </div>
          </>
        )}
        <span className="ol-name">
          <span className="strong">{nameOf(names, request.employeeId)}</span>
          <span className="t-xs muted">{t('record.hr.leave.thisRequest')}</span>
        </span>
        <div className="ol-track">{bar(request.from, request.to, 'ol-bar is-mine', t('record.hr.leave.thisRequest'))}</div>
        {others.map((row) => {
          // The clash segment is the intersection with this request; the rest of the
          // colleague's absence is drawn plain.
          const clashFrom = row.from > request.from ? row.from : request.from
          const clashTo = row.to < request.to ? row.to : request.to
          return (
            <OverlapRow key={row.id} name={nameOf(names, row.employeeId)} sub={`${typeLabel(row.type, t)} · ${day(row.from, locale)} – ${day(row.to, locale)}`} status={row.status}>
              {bar(row.from, row.to, 'ol-bar', '')}
              {bar(clashFrom, clashTo, 'ol-bar is-clash', t('record.hr.leave.clash'))}
            </OverlapRow>
          )
        })}
      </div>
      {scaled && (
        <div className="row-wrap t-xs muted" style={{ gap: '6px 16px' }}>
          <span className="row" style={{ gap: 6 }}>
            <span className="ol-bar is-mine hr-legend" />
            {t('record.hr.leave.thisRequest')}
          </span>
          <span className="row" style={{ gap: 6 }}>
            <span className="ol-bar is-clash hr-legend" />
            {t('record.hr.leave.legendClash')}
          </span>
          <span>{t('record.hr.leave.legendWeekend')}</span>
        </div>
      )}
    </div>
  )
}

function OverlapRow({ name, sub, status, children }: { name: string; sub: string; status: string; children: React.ReactNode }) {
  return (
    <>
      <span className="ol-name">
        <span>{name}</span>
        <span className="t-xs muted">
          {sub} · <CellValue values={{ status }} column="status" descriptor={{ name: 'status', type: 'text', sortable: false, filterable: false }} entity="hr.leave" />
        </span>
      </span>
      <div className="ol-track">{children}</div>
    </>
  )
}

// ------------------------------------------------------------------ маршрут

/**
 * Маршрут согласования (`.chain`).
 *
 * Строится **на сервере** тем же правилом, что и при подаче, — по сотруднику и **периоду**:
 * рабочие дни считает производственный календарь сервера (#81). Присланный клиентом маршрут
 * означал бы возможность согласовать себе отпуск самому.
 *
 * Шаг адресован учётной записи («именно он») или роли («кто-нибудь из») — и называется
 * по-разному. Пустой маршрут — не ошибка: у руководителя может не быть учётной записи, и это
 * сказано словами.
 */
export function RoutePanel({ request }: { request: LeaveRequestCard }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['hr-leave-route', request.employeeId, request.from, request.to],
    queryFn: ({ signal }) => fetchRoute(request.employeeId, request.from, request.to, signal),
    retry: false,
  })
  const approved = request.status === 'approved'
  const closed = request.status === 'rejected' || request.status === 'cancelled'

  return (
    <Panel title={t('card.tab.hrRoute')} flush>
      <Section query={query} errorTitle={t('record.hr.leave.routeFailed')}>
        {(steps: RouteStepView[]) => (
          <>
            <div className="panel-b">
              <div className="chain">
                <div className="chain-step is-done">
                  <span className="chain-dot">
                    <Icon name="check" />
                  </span>
                  <span className="strong">{t('record.hr.leave.submitted')}</span>
                  <span className="badge b-success">{t('record.hr.leave.submitted')}</span>
                </div>
                {steps.length === 0 ? (
                  <div className="chain-step is-skipped">
                    <span className="chain-dot">—</span>
                    <span className="secondary">{t('card.hr.leave.noRoute')}</span>
                    <span />
                  </div>
                ) : (
                  [...steps]
                    .sort((a, b) => a.order - b.order)
                    .map((step) => (
                      <div key={`${step.order}-${step.name}`} className={approved ? 'chain-step is-done' : 'chain-step'}>
                        <span className="chain-dot">{approved ? <Icon name="check" /> : step.order}</span>
                        <span className="stack" style={{ gap: 0 }}>
                          <span className="strong">{step.name}</span>
                          <span className="t-sm secondary">
                            {step.approverUserId ? t('record.hr.leave.approverUser') : t('record.hr.leave.approverRole')}
                          </span>
                        </span>
                        <span className={approved ? 'badge b-success' : 'badge'}>
                          {approved ? t('value.hr.leave.status.approved') : closed ? '—' : t('record.hr.leave.waiting')}
                        </span>
                      </div>
                    ))
                )}
                {closed && (
                  <div className="chain-step is-rejected">
                    <span className="chain-dot">
                      <Icon name="close" />
                    </span>
                    <span className="strong">{t(`value.hr.leave.status.${request.status}`)}</span>
                    <span className="badge b-danger">{t(`value.hr.leave.status.${request.status}`)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="panel-f">
              <Icon name="alert" />
              <span className="secondary">{t('record.hr.leave.routeNote')}</span>
            </div>
          </>
        )}
      </Section>
    </Panel>
  )
}

// ------------------------------------------------------------------ табель

/**
 * Табель за период заявки — сеткой дней.
 *
 * Строка на каждый день: пропуск неотличим между «отсутствовал» и «никто не заполнил».
 * Табель заполняется при согласовании; пустой означает, что заявка ещё не согласована, и это
 * сказано словами.
 */
export function TimesheetPanel({ employeeId, from, to }: { employeeId: string; from: string; to: string }) {
  const { t, locale } = useApp()
  const query = useQuery({
    queryKey: ['hr-timesheet', employeeId, from, to],
    queryFn: ({ signal }) => fetchTimesheet(employeeId, from, to, signal),
    retry: false,
  })
  return (
    <Panel title={t('record.hr.leave.timesheetTitle', { from: day(from, locale), to: day(to, locale) })}>
      <Section query={query} errorTitle={t('record.hr.leave.timesheetFailed')}>
        {(rows: TimeEntryView[]) =>
          rows.length === 0 ? (
            <StateView compact kind="empty" title={t('card.hr.leave.noTimesheet')} description={t('record.hr.leave.noTimesheetText')} />
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              <div role="list" aria-label={t('card.tab.hrTimesheet')} className="hr-timesheet">
                {[...rows]
                  .sort((a, b) => a.day.localeCompare(b.day))
                  .map((row) => (
                    <span
                      key={row.id}
                      role="listitem"
                      className={`hr-day is-${row.type}`}
                      aria-label={`${day(row.day, locale)}: ${t(`hr.timesheet.type.${row.type}`)}, ${num(row.hoursWorked, locale)}`}
                      title={row.comment ?? undefined}
                    >
                      <span className="t-xs muted">{parseDay(row.day)?.getDate() ?? row.day}</span>
                      <span className="mono hr-day-code">{t(`record.hr.timesheet.code.${row.type}`)}</span>
                    </span>
                  ))}
              </div>
              <div className="row-wrap t-xs secondary" style={{ gap: '4px 16px' }}>
                {[...new Set(rows.map((row) => row.type))].map((type) => (
                  <span key={type}>
                    <span className="mono strong">{t(`record.hr.timesheet.code.${type}`)}</span> — {t(`hr.timesheet.type.${type}`)}
                  </span>
                ))}
              </div>
            </div>
          )
        }
      </Section>
    </Panel>
  )
}

// ------------------------------------------------------------------ мелочи

function typeLabel(type: string, t: Translate): string {
  return type ? t(`value.hr.leave.leave_type.${type}`) : '—'
}

function nextDay(iso: string): string {
  const date = parseDay(iso)
  if (!date) return iso
  return isoDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1))
}

function dayOfMoment(value: string, locale: 'ru' | 'en'): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  return day(isoDate(moment), locale)
}
